/**
 * Permissions Context
 *
 * Manages role-based access control (RBAC) for tenant-specific features.
 * Supports warehouse_operator, warehouse_viewer, and admin roles.
 * Enforces tenant-based feature visibility (e.g., warehouse only for Nespresso).
 */

import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { useAuth } from "./auth-context";
import { useTenant } from "./tenant-context";

// User roles for granular permissions
export type UserRole =
  | "admin" // Full access to all features
  | "warehouse_operator" // Can process packages, view warehouse data
  | "warehouse_viewer" // Read-only access to warehouse data
  | "tenant_admin" // Full access within tenant
  | "tenant_user"; // Standard tenant user access

// Permission types for fine-grained control
export type Permission =
  // Warehouse permissions
  | "warehouse:view" // View warehouse dashboard and package list
  | "warehouse:process" // Start/complete package processing
  | "warehouse:flag" // Flag packages with issues
  | "warehouse:metrics" // View warehouse metrics
  // Analytics permissions
  | "analytics:view"
  | "analytics:export"
  // Product permissions
  | "products:view"
  | "products:edit"
  // Program permissions
  | "programs:view"
  | "programs:edit";

// Features that are tenant-specific
export type TenantFeature =
  | "warehouse" // Only for Nespresso
  | "recycling" // Only for Nespresso
  | "nespresso_analytics"; // Only for Nespresso

// Tenant feature configuration
const TENANT_FEATURES: Record<string, TenantFeature[]> = {
  nespresso: ["warehouse", "recycling", "nespresso_analytics"],
  // Other tenants don't have warehouse/recycling features
};

// Role-based permission mapping
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    "warehouse:view",
    "warehouse:process",
    "warehouse:flag",
    "warehouse:metrics",
    "analytics:view",
    "analytics:export",
    "products:view",
    "products:edit",
    "programs:view",
    "programs:edit",
  ],
  warehouse_operator: [
    "warehouse:view",
    "warehouse:process",
    "warehouse:flag",
    "warehouse:metrics",
    "analytics:view",
  ],
  warehouse_viewer: ["warehouse:view", "warehouse:metrics", "analytics:view"],
  tenant_admin: [
    "warehouse:view",
    "warehouse:process",
    "warehouse:flag",
    "warehouse:metrics",
    "analytics:view",
    "analytics:export",
    "products:view",
    "products:edit",
    "programs:view",
    "programs:edit",
  ],
  tenant_user: [
    "warehouse:view",
    "warehouse:metrics",
    "analytics:view",
    "products:view",
    "programs:view",
  ],
};

const PLAN_ORDER = ['free', 'trial', 'starter', 'growth', 'enterprise'];

interface PermissionsContextType {
  // Role-based checks
  roles: UserRole[];
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;

  // Permission-based checks
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;

  // Feature visibility (tenant-specific)
  hasFeature: (feature: TenantFeature) => boolean;
  availableFeatures: TenantFeature[];

  // Plan-based gating
  plan: string;
  hasMinPlan: (required: string) => boolean;
  isFree: boolean;

  // Convenience checks
  canViewWarehouse: boolean;
  canProcessPackages: boolean;
  canFlagPackages: boolean;
  isWarehouseOperator: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

// Storage key for user roles (demo purposes - in production this comes from API)
const ROLES_STORAGE_KEY = "pic_user_roles";

interface Props {
  children: ReactNode;
}

export function PermissionsProvider({ children }: Props) {
  const { user, isAuthenticated } = useAuth();
  const { tenantId } = useTenant();

  // Compute user roles
  const roles = useMemo<UserRole[]>(() => {
    if (!isAuthenticated || !user) return [];

    // Admin users get admin role
    if (user.isAdmin) return ["admin"];

    // Check stored roles (for demo purposes)
    const storedRoles = localStorage.getItem(ROLES_STORAGE_KEY);
    if (storedRoles) {
      try {
        const parsed = JSON.parse(storedRoles);
        if (Array.isArray(parsed)) {
          return parsed.filter((r): r is UserRole =>
            [
              "admin",
              "warehouse_operator",
              "warehouse_viewer",
              "tenant_admin",
              "tenant_user",
            ].includes(r)
          );
        }
      } catch {
        // Ignore parsing errors
      }
    }

    // Default: For Nespresso tenant users, grant warehouse_viewer by default
    // For demo purposes, we'll grant operator access to show full functionality
    if (tenantId === "nespresso") {
      return ["warehouse_operator", "tenant_user"];
    }

    return ["tenant_user"];
  }, [isAuthenticated, user, tenantId]);

  // Compute permissions from roles
  const permissions = useMemo<Permission[]>(() => {
    const permSet = new Set<Permission>();
    for (const role of roles) {
      const rolePerms = ROLE_PERMISSIONS[role] || [];
      for (const perm of rolePerms) {
        permSet.add(perm);
      }
    }
    return Array.from(permSet);
  }, [roles]);

  // Compute available features for current tenant
  const availableFeatures = useMemo<TenantFeature[]>(() => {
    return TENANT_FEATURES[tenantId] || [];
  }, [tenantId]);

  // Plan-based gating
  const plan = user?.tenantPlan ?? (user?.isAdmin ? 'enterprise' : 'free');
  const hasMinPlan = (required: string): boolean => {
    if (user?.isAdmin) return true;
    return PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(required);
  };
  const isFree = plan === 'free';

  // Role check functions
  const hasRole = (role: UserRole): boolean => roles.includes(role);

  const hasAnyRole = (checkRoles: UserRole[]): boolean =>
    checkRoles.some((r) => roles.includes(r));

  // Permission check functions
  const hasPermission = (permission: Permission): boolean =>
    permissions.includes(permission);

  const hasAllPermissions = (checkPerms: Permission[]): boolean =>
    checkPerms.every((p) => permissions.includes(p));

  const hasAnyPermission = (checkPerms: Permission[]): boolean =>
    checkPerms.some((p) => permissions.includes(p));

  // Feature check (tenant-specific)
  const hasFeature = (feature: TenantFeature): boolean =>
    availableFeatures.includes(feature);

  // Convenience checks
  const canViewWarehouse =
    hasFeature("warehouse") && hasPermission("warehouse:view");
  const canProcessPackages =
    hasFeature("warehouse") && hasPermission("warehouse:process");
  const canFlagPackages =
    hasFeature("warehouse") && hasPermission("warehouse:flag");
  const isWarehouseOperator =
    hasRole("warehouse_operator") || hasRole("admin") || hasRole("tenant_admin");

  return (
    <PermissionsContext.Provider
      value={{
        roles,
        hasRole,
        hasAnyRole,
        permissions,
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasFeature,
        availableFeatures,
        plan,
        hasMinPlan,
        isFree,
        canViewWarehouse,
        canProcessPackages,
        canFlagPackages,
        isWarehouseOperator,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
}

// Utility to set user roles (for demo/testing purposes)
export function setUserRoles(roles: UserRole[]): void {
  localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
  // Trigger a storage event so other components can react
  window.dispatchEvent(new Event("storage"));
}

// Utility to clear user roles
export function clearUserRoles(): void {
  localStorage.removeItem(ROLES_STORAGE_KEY);
  window.dispatchEvent(new Event("storage"));
}
